import { brandPalette } from "../../constants/brandPalette";
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const normalizeRect = (start, end, bounds) => {
  const x1 = clamp(Math.min(start.x, end.x), 0, bounds.width);
  const y1 = clamp(Math.min(start.y, end.y), 0, bounds.height);
  const x2 = clamp(Math.max(start.x, end.x), 0, bounds.width);
  const y2 = clamp(Math.max(start.y, end.y), 0, bounds.height);

  return {
    x: x1,
    y: y1,
    width: Math.max(x2 - x1, 1),
    height: Math.max(y2 - y1, 1),
  };
};

const cropImageToBase64 = (image, crop, mimeType = "image/jpeg") => {
  const scaleX = image.naturalWidth / image.clientWidth;
  const scaleY = image.naturalHeight / image.clientHeight;
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(crop.width * scaleX);
  canvas.height = Math.round(crop.height * scaleY);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    canvas.width,
    canvas.height
  );
  const dataUrl = canvas.toDataURL(mimeType, 0.92);
  return dataUrl.split(",")[1];
};

function QuestionImageCropEditor({ isOpen, title, src, mimeType, onApply, onClose }) {
  const imgRef = useRef(null);
  const [bounds, setBounds] = useState({ width: 0, height: 0 });
  const [crop, setCrop] = useState(null);
  const [dragStart, setDragStart] = useState(null);

  const syncBounds = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;
    setBounds({ width: img.clientWidth, height: img.clientHeight });
    setCrop({ x: 0, y: 0, width: img.clientWidth, height: img.clientHeight });
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setCrop(null);
      setDragStart(null);
    }
  }, [isOpen]);

  const getRelativePoint = (event) => {
    const rect = imgRef.current.getBoundingClientRect();
    return {
      x: clamp(event.clientX - rect.left, 0, rect.width),
      y: clamp(event.clientY - rect.top, 0, rect.height),
    };
  };

  const handlePointerDown = (event) => {
    if (!imgRef.current) return;
    event.preventDefault();
    const point = getRelativePoint(event);
    setDragStart(point);
    setCrop({ x: point.x, y: point.y, width: 1, height: 1 });
  };

  const handlePointerMove = (event) => {
    if (!dragStart || !imgRef.current) return;
    event.preventDefault();
    const point = getRelativePoint(event);
    setCrop(normalizeRect(dragStart, point, bounds));
  };

  const handlePointerUp = () => {
    setDragStart(null);
  };

  const handleSelectAll = () => {
    if (!imgRef.current) return;
    setCrop({
      x: 0,
      y: 0,
      width: imgRef.current.clientWidth,
      height: imgRef.current.clientHeight,
    });
  };

  const handleApply = () => {
    const img = imgRef.current;
    if (!img || !crop || crop.width < 4 || crop.height < 4) return;
    const base64 = cropImageToBase64(img, crop, mimeType || "image/jpeg");
    onApply({
      image_base64: base64,
      mime_type: mimeType || "image/jpeg",
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="var(--surface-raised)" maxW="min(95vw, 1100px)">
        <ModalHeader color="var(--text-primary)">{title || "Crop question image"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="var(--text-muted)" mb={3}>
            Drag on the image to select the area you want to keep.
          </Text>
          <Box
            position="relative"
            display="inline-block"
            maxW="100%"
            mx="auto"
            userSelect="none"
            touchAction="none"
            onMouseLeave={handlePointerUp}
            onMouseUp={handlePointerUp}
            onMouseMove={handlePointerMove}
            onTouchEnd={handlePointerUp}
            onTouchMove={(e) => handlePointerMove(e.touches[0])}
          >
            <Box
              as="img"
              ref={imgRef}
              src={src}
              alt="Crop preview"
              display="block"
              maxH="70vh"
              maxW="100%"
              mx="auto"
              draggable={false}
              onLoad={syncBounds}
              onMouseDown={handlePointerDown}
              onTouchStart={(e) => handlePointerDown(e.touches[0])}
              cursor="crosshair"
            />
            {crop && bounds.width > 0 ? (
              <>
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  w={`${bounds.width}px`}
                  h={`${bounds.height}px`}
                  pointerEvents="none"
                  bg="rgba(0,0,0,0.45)"
                  style={{
                    clipPath: `polygon(0% 0%, 0% 100%, ${crop.x}px 100%, ${crop.x}px ${crop.y}px, ${
                      crop.x + crop.width
                    }px ${crop.y}px, ${crop.x + crop.width}px ${crop.y + crop.height}px, ${crop.x}px ${
                      crop.y + crop.height
                    }px, ${crop.x}px 100%, 100% 100%, 100% 0%)`,
                  }}
                />
                <Box
                  position="absolute"
                  left={`${crop.x}px`}
                  top={`${crop.y}px`}
                  w={`${crop.width}px`}
                  h={`${crop.height}px`}
                  border="2px solid"
                  borderColor={brandPalette.primary}
                  boxShadow="0 0 0 9999px rgba(0,0,0,0)"
                  pointerEvents="none"
                />
              </>
            ) : null}
          </Box>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" fontWeight="700" onClick={handleSelectAll}>
            Select all
          </Button>
          <Button variant="ghost" fontWeight="700" onClick={onClose}>
            Cancel
          </Button>
          <Button
            bg={brandPalette.primary}
            color="white"
            fontWeight="800"
            onClick={handleApply}
            isDisabled={!crop || crop.width < 4 || crop.height < 4}
            _hover={{ bg: brandPalette.accentDark }}
          >
            Apply crop
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default QuestionImageCropEditor;
